import React, { useState, useMemo, useEffect } from "react";
import { Card, CardHeader, CardBody, CardExpandToggler } from "../../../components/card/card.jsx";
import getPentestServer from "../utils/getPentestServer";
import authService from '../../../services/authService';

// Format date utility
function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString();
  } catch {
    return d;
  }
}

// Table column definitions
const columns = [
  { key: "checkbox", label: "", adminOnly: true },
  { key: "expand", label: "" },
  { key: "id", label: "# Scan ID" },
  { key: "target_url", label: "Target URL" },
  { key: "status", label: "Status" },
  { key: "username", label: "User" },
  { key: "user_ip", label: "User IP" },
  { key: "start_time", label: "Start Time" },
  { key: "end_time", label: "End Time" },
  { key: "options", label: "Options" },
  { key: "actions", label: "Actions", adminOnly: true }
];

// Sort helper
const sortTable = (data, key, asc) => {
  const sorted = [...data].sort((a, b) => {
    if (a[key] === b[key]) return 0;
    if (!a[key]) return 1;
    if (!b[key]) return -1;
    if (typeof a[key] === "string") {
      return asc ? a[key].localeCompare(b[key]) : b[key].localeCompare(a[key]);
    }
    if (typeof a[key] === "number") {
      return asc ? a[key] - b[key] : b[key] - a[key];
    }
    if (a[key] instanceof Date && b[key] instanceof Date) {
      return asc ? a[key] - b[key] : b[key] - a[key];
    }
    return 0;
  });
  return sorted;
};

export default function ScanResults({
  tableData,
  selectedScans,
  userRole,
  isDeleting,
  handlers,
  generatingPdf,
  pdfStatuses,
  currentUser,
}) {
  // Sort and filter states
  const [sortKey, setSortKey] = useState("start_time");
  const [sortAsc, setSortAsc] = useState(false);
  const [filter, setFilter] = useState("");
  // Expanded row state
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedFiles, setExpandedFiles] = useState({}); // scanId -> { loading, files, error }

  // RBAC-scoped filters
  const [allUsers, setAllUsers] = useState([]); // from authService.getAllUsers
  const [orgFilter, setOrgFilter] = useState('all'); // admin only; superuser fixed to their org
  const [userFilter, setUserFilter] = useState('all');

  const isAdmin = userRole === "admin";
  const isSuperuser = userRole === "superuser";
  const isPrivileged = isAdmin || isSuperuser;

  const allSelected = selectedScans.size > 0 && selectedScans.size === tableData.length;

  // Load users list for filters (admin/superuser)
  useEffect(() => {
    let cancelled = false;
    async function loadUsers() {
      if (!isPrivileged) return;
      try {
        const users = await authService.getAllUsers();
        if (!cancelled) setAllUsers(Array.isArray(users) ? users : []);
      } catch (e) {
        if (!cancelled) setAllUsers([]);
        // silently ignore if forbidden; server-side will enforce scoping
      }
    }
    loadUsers();
    return () => { cancelled = true; };
  }, [isPrivileged]);

  // Initialize org filter for superuser to their organisation
  useEffect(() => {
    if (isSuperuser) {
      const org = currentUser?.organisation || currentUser?.organization || '';
      setOrgFilter(org || 'all');
    } else if (isAdmin) {
      setOrgFilter(prev => prev); // keep selection
    } else {
      setOrgFilter('all');
      setUserFilter('all');
    }
  }, [isSuperuser, isAdmin, currentUser]);

  // Build usersByOrg mapping and option lists
  const usersByOrg = useMemo(() => {
    const map = {};
    (allUsers || []).forEach(u => {
      const org = u?.organisation || u?.organization || '';
      const name = u?.username || '';
      if (!name) return;
      if (!map[org]) map[org] = [];
      map[org].push(name);
    });
    return map;
  }, [allUsers]);

  const orgOptions = useMemo(() => {
    const options = Object.keys(usersByOrg).filter(Boolean).sort();
    if (isAdmin) return ['all', ...options];
    if (isSuperuser) {
      const org = currentUser?.organisation || currentUser?.organization || '';
      return org ? [org] : [];
    }
    return [];
  }, [usersByOrg, isAdmin, isSuperuser, currentUser]);

  const userOptions = useMemo(() => {
    if (!isPrivileged) return [];
    if (isAdmin) {
      if (orgFilter && orgFilter !== 'all') return ['all', ...((usersByOrg[orgFilter] || []).sort())];
      // all orgs -> all users
      const all = Object.values(usersByOrg).flat();
      return ['all', ...Array.from(new Set(all)).sort()];
    }
    // superuser -> users of their org
    const org = currentUser?.organisation || currentUser?.organization || '';
    return ['all', ...((usersByOrg[org] || []).sort())];
  }, [isPrivileged, isAdmin, orgFilter, usersByOrg, currentUser]);

  // Apply role-based scoping and UI filters BEFORE text filter/search
  const roleScopedData = useMemo(() => {
    let data = Array.isArray(tableData) ? tableData : [];

    // Base RBAC scope
    if (userRole === 'user') {
      const me = (currentUser?.username || '').toLowerCase();
      data = data.filter(r => (r?.username || '').toLowerCase() === me);
    } else if (userRole === 'superuser') {
      const org = currentUser?.organisation || currentUser?.organization || '';
      const allowedUsers = new Set((usersByOrg[org] || []).map(n => (n || '').toLowerCase()));
      if (allowedUsers.size > 0) {
        data = data.filter(r => allowedUsers.has((r?.username || '').toLowerCase()));
      }
      // if we couldn't load users (empty set), rely on server-side scoping (no client filter)
    }

    // UI filters
    if (isAdmin) {
      if (orgFilter && orgFilter !== 'all') {
        const allowed = new Set((usersByOrg[orgFilter] || []).map(n => (n || '').toLowerCase()));
        data = data.filter(r => allowed.has((r?.username || '').toLowerCase()));
      }
      if (userFilter && userFilter !== 'all') {
        const sel = (userFilter || '').toLowerCase();
        data = data.filter(r => (r?.username || '').toLowerCase() === sel);
      }
    } else if (isSuperuser) {
      if (userFilter && userFilter !== 'all') {
        const sel = (userFilter || '').toLowerCase();
        data = data.filter(r => (r?.username || '').toLowerCase() === sel);
      }
    }

    return data;
  }, [tableData, userRole, currentUser, usersByOrg, isAdmin, isSuperuser, orgFilter, userFilter]);

  // Filtering rows by search input (search in all columns except actions/checkbox/expand)
  const filteredData = useMemo(() => {
    if (!filter) return roleScopedData;
    const lcFilter = filter.toLowerCase();
    return roleScopedData.filter(row =>
      Object.entries(row)
        .filter(([k]) => !["options"].includes(k))
        .map(([k, v]) => (v ? String(v) : ""))
        .join(" ")
        .toLowerCase()
        .includes(lcFilter)
    );
  }, [roleScopedData, filter]);

  // Sorting rows based on current sort column
  const sortedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    if (!sortKey || !filteredData[0][sortKey]) return filteredData;
    return sortTable(filteredData, sortKey, sortAsc);
  }, [filteredData, sortKey, sortAsc]);

  // Sorting click handler
  function handleSort(key) {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  }

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const pageSizes = [5, 10, 20, 50, 100];

  const totalRows = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  React.useEffect(() => { setPage(1); }, [filter, sortKey, sortAsc, rowsPerPage, orgFilter, userFilter]);

  // Expand/collapse file list for a scan
  async function handleExpandRow(scanId) {
    if (expandedRow === scanId) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(scanId);

    // Do not re-fetch if files already loaded
    if (expandedFiles[scanId] && expandedFiles[scanId].files) return;

    setExpandedFiles(prev => ({
      ...prev,
      [scanId]: { loading: true, files: null, error: null }
    }));

    try {
      const res = await authService.authenticatedCall(`${getPentestServer()}/api/results/files/${scanId}`);
      if (!res.ok) throw new Error("Server error: " + res.status);
      const data = await res.json();
      setExpandedFiles(prev => ({
        ...prev,
        [scanId]: { loading: false, files: data.files || [], error: null }
      }));
    } catch (e) {
      setExpandedFiles(prev => ({
        ...prev,
        [scanId]: { loading: false, files: null, error: e.message }
      }));
    }
  }

  // File view handler (calls parent handler)
  function handleFileView(file, scanId) {
    if (handlers.onViewFile) {
      handlers.onViewFile({ name: file.name, scanId });
    }
  }

  return (
    <Card>
      {/* Inline styles for file links */}
      <style>{`
        .scan-file-link {
          text-decoration: none;
          font-weight: 500;
          transition: background 0.15s, color 0.15s;
          padding: 2px 4px;
          border-radius: 3px;
        }
        .scan-file-link:hover,
        .scan-file-link:focus {
          background:rgba(177, 177, 177, 0.51);
          text-decoration: none;
        }
      `}</style>
      <CardHeader className="fw-bold small text-white bg-dark">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            <i className="fa fa-table me-2"></i>Scan Results
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-sm btn-outline-light"
              onClick={handlers.refreshTableData}
              title="Refresh results data"
              data-bs-toggle="tooltip"
              data-bs-placement="bottom"
            >
              <i className="fa fa-refresh"></i>
            </button>
            {isPrivileged && (
              <button
                className="btn btn-sm btn-outline-warning"
                onClick={handlers.onClearTable}
                title="Clear table display (does not delete actual data)"
                data-bs-toggle="tooltip"
                data-bs-placement="bottom"
              >
                <i className="fa fa-eraser"></i>
              </button>
            )}
            <CardExpandToggler />
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {/* Filter input + RBAC filters */}
        <div className="mb-3 d-flex align-items-center gap-2 flex-wrap" style={{maxWidth: '100%'}}>
          {/* RBAC filter controls */}
          {isAdmin && (
            <>
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0" style={{whiteSpace:'nowrap'}}>Organisation:</label>
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: 180 }}
                  value={orgFilter}
                  onChange={e => { setOrgFilter(e.target.value); setUserFilter('all'); }}
                >
                  {orgOptions.map(opt => (
                    <option key={opt} value={opt}>{opt === 'all' ? 'All organisations' : opt}</option>
                  ))}
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0" style={{whiteSpace:'nowrap'}}>User:</label>
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: 180 }}
                  value={userFilter}
                  onChange={e => setUserFilter(e.target.value)}
                >
                  {userOptions.map(opt => (
                    <option key={opt} value={opt}>{opt === 'all' ? 'All users' : opt}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          {isSuperuser && (
            <>
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-secondary" title="Your organisation" style={{whiteSpace:'nowrap'}}>
                  {(currentUser?.organisation || currentUser?.organization) || 'No organisation'}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0" style={{whiteSpace:'nowrap'}}>User:</label>
                <select
                  className="form-select form-select-sm"
                  style={{ minWidth: 180 }}
                  value={userFilter}
                  onChange={e => setUserFilter(e.target.value)}
                >
                  {userOptions.map(opt => (
                    <option key={opt} value={opt}>{opt === 'all' ? 'All users' : opt}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Text filter */}
          <input
            type="text"
            className="form-control"
            placeholder="Filter results…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{minWidth: 0, flex: 1, maxWidth: 240}}
          />
          {isPrivileged && (
            <>
              <input
                type="checkbox"
                className="form-check-input"
                id="selectAll"
                onChange={e => handlers.onSelectAll(e.target.checked)}
                checked={allSelected}
                title="Select or deselect all scans"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                style={{marginLeft: 8, marginRight: 4}}
              />
              <label className="form-check-label me-2" htmlFor="selectAll" style={{whiteSpace: "nowrap", fontSize: "0.95em"}}>
                Select All ({selectedScans.size})
              </label>
              {selectedScans.size > 0 && (
                <button
                  className="btn btn-sm btn-danger"
                  onClick={handlers.handleDeleteSelected}
                  disabled={isDeleting}
                  title={`Delete ${selectedScans.size} selected scan(s) permanently`}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  style={{marginLeft: 8}}
                >
                  {isDeleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash me-1"></i>
                      Delete ({selectedScans.size})
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>
        {/* Table */}
        <div className="table-responsive">
          <table className="table table-striped table-bordered align-middle text-nowrap" style={{ width: "100%" }}>
            <thead>
              <tr>
                {columns.map(col =>
                  (!col.adminOnly || isPrivileged) && (
                    <th
                      key={col.key}
                      onClick={() =>
                        col.key !== "checkbox" &&
                        col.key !== "expand" &&
                        col.key !== "actions" &&
                        handleSort(col.key)
                      }
                      style={{
                        cursor:
                          col.key !== "checkbox" &&
                          col.key !== "expand" &&
                          col.key !== "actions"
                            ? "pointer"
                            : "default"
                      }}
                    >
                      {col.label}
                      {sortKey === col.key && (
                        <span>{sortAsc ? " ▲" : " ▼"}</span>
                      )}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {pagedRows.map(row => (
                <React.Fragment key={row.id}>
                  <tr>
                    {/* Checkbox */}
                    {isPrivileged && (
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input scan-checkbox"
                          checked={selectedScans.has(row.id)}
                          onChange={e => handlers.onScanSelection(row.id, e.target.checked)}
                        />
                      </td>
                    )}
                    {/* Expand/collapse arrow */}
                    <td>
                      <button
                        className="btn btn-sm"
                        title="Expand row and load scan files"
                        onClick={() => handleExpandRow(row.id)}
                        style={{ padding: "0.2em" }}
                      >
                        <i className={`bi ${expandedRow === row.id ? "bi-chevron-down" : "bi-chevron-right"}`}></i>
                      </button>
                    </td>
                    <td>{row.id}</td>
                    <td>{row.target_url}</td>
                    <td>{row.status}</td>
                    <td>{row.username || "Anonymous"}</td>
                    <td>{row.user_ip && row.user_ip.replace(/^::ffff:/, "")}</td>
                    <td>{formatDate(row.start_time)}</td>
                    <td>{formatDate(row.end_time)}</td>
                    <td>{row.options}</td>
                    {/* Actions */}
                    {isPrivileged && (
                      <td>
                        {row.status === "completed" && (
                          <button
                            className="btn btn-sm btn-success me-2"
                            title="Generate AI PDF Report"
                            disabled={generatingPdf?.has?.(row.id)}
                            onClick={(e) => handlers.onGeneratePdf(e, row.id)}
                          >
                            {generatingPdf?.has?.(row.id) ? (
                              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            ) : (
                              <i className="bi bi-file-earmark-pdf"></i>
                            )}
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-danger"
                          title="Delete Scan"
                          onClick={() => handlers.onDeleteScan(row.id)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    )}
                  </tr>
                  {/* Expanded row: show files from server */}
                  {expandedRow === row.id && (
                    <tr>
                      <td colSpan={isPrivileged ? columns.length : columns.length - 1}>
                        <div>
                          <strong>Scan result files:</strong>
                          {expandedFiles[row.id]?.loading && (
                            <span className="ms-2">Loading files...</span>
                          )}
                          {expandedFiles[row.id]?.error && (
                            <span className="text-danger ms-2">Error: {expandedFiles[row.id].error}</span>
                          )}
                          {expandedFiles[row.id]?.files?.length === 0 && !expandedFiles[row.id]?.loading && (
                            <span className="ms-2">No files for this scan.</span>
                          )}
                          {expandedFiles[row.id]?.files?.length > 0 && (
                            <ul className="list-group mt-2">
                              {expandedFiles[row.id].files.map(file => (
                                <li className="list-group-item d-flex align-items-center" key={file.name}>
                                  <i className="bi bi-file-text me-2"></i>
                                  <a
                                    href="#"
                                    onClick={e => {
                                      e.preventDefault();
                                      handleFileView(file, row.id);
                                    }}
                                    className="scan-file-link"
                                  >
                                    {file.name}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <div>
                <span className="me-2">Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={e => setRowsPerPage(Number(e.target.value))}
                  className="form-select d-inline-block"
                  style={{ width: 80, display: "inline-block" }}
                >
                  {pageSizes.map(size => (
                    <option value={size} key={size}>{size}</option>
                  ))}
                </select>
                <span className="ms-3">
                  Showing {Math.min((page - 1) * rowsPerPage + 1, totalRows)}
                  –
                  {Math.min(page * rowsPerPage, totalRows)}
                  {" of "}
                  {totalRows}
                </span>
              </div>
              <div>
                <button
                  className="btn btn-sm btn-outline-secondary me-1"
                  disabled={page === 1}
                  onClick={() => setPage(1)}
                  title="First page"
                >{"<<"}</button>
                <button
                  className="btn btn-sm btn-outline-secondary me-1"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  title="Previous page"
                >{"<"}</button>
                <span style={{ minWidth: 70, display: "inline-block", textAlign: "center" }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  className="btn btn-sm btn-outline-secondary ms-1"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  title="Next page"
                >{" >".trim()}</button>
                <button
                  className="btn btn-sm btn-outline-secondary ms-1"
                  disabled={page === totalPages}
                  onClick={() => setPage(totalPages)}
                  title="Last page"
                >{">>"}</button>
              </div>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
